import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useApp } from "@/state/AppContext";

/**
 * U10 (UAT 2026-10-29): the ONE "are you sure?" dialog. Destructive or hard-to-undo controls (cancel
 * an invoice, remove a contact, retire a batch, switch role) ask first through this instead of each
 * screen assembling its own AlertDialog.
 *
 * Controlled: the caller owns `open` and decides what `onConfirm` does (it may be async - the
 * confirm button is disabled while `busy`). `destructive` paints the confirm button red so a
 * delete never looks like a routine "OK". The dialog stays open until the caller closes it, so a
 * failed action can keep the prompt (and its error toast) in view.
 *
 * `children` render under the body (RF-17/U10: the credit-note dialog asks for its reason here) and
 * `confirmDisabled` holds the confirm button off until that input is valid.
 *
 * `testIdPrefix` names the three hooks - `{prefix}-dialog`, `{prefix}-cancel`, `{prefix}-ok`; a
 * screen that already shipped differently-named ids passes `testIds={{ dialog, cancel, ok }}`.
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = false,
  busy = false,
  confirmDisabled = false,
  children,
  onConfirm,
  testIdPrefix = "confirm",
  testIds,
}) {
  const { t } = useApp();
  const ids = {
    dialog: `${testIdPrefix}-dialog`,
    cancel: `${testIdPrefix}-cancel`,
    ok: `${testIdPrefix}-ok`,
    ...testIds,
  };
  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange?.(next)}>
      <AlertDialogContent data-testid={ids.dialog}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {body && <AlertDialogDescription>{body}</AlertDialogDescription>}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={ids.cancel} disabled={busy}>
            {cancelLabel || t("common.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={ids.ok}
            data-destructive={destructive ? "true" : undefined}
            disabled={busy || confirmDisabled}
            className={cn(
              destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
            onClick={(e) => {
              // Radix closes an AlertDialog on Action click; hold it open until the caller decides.
              e.preventDefault();
              onConfirm?.();
            }}
          >
            {confirmLabel || t("ownerConfirm.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
